/**
 * The real game: loading screen, MegaMotocross-branded menus over the live 3D scene, rides, results. The tuning
 * harness lives at /dev.html; old harness URLs (?view=..., &demo=1, ...) are forwarded there so scripts/shot.mjs
 * and scripts/bench.mjs keep working.
 */
import * as THREE from 'three/webgpu';
import { MessageKey } from '../i18n';
import { InputFrame } from '../sim/input';
import { LEVELS, LevelId } from '../sim/levels';
import { KeyboardInput } from '../dev/keyboard';
import { TouchInput } from '../dev/touch';
import { emptyProgress } from '../game/progress';
import { padRiding } from '../ui/logic/gamepad';
import { ScreenId } from '../ui/logic/screens';
import { Ride, RideResult, UiHost } from '../ui/types';
import { GameUi } from '../ui/ui';
import { GameSession } from './session';
import { clearWorld, saveWorld, storage } from './worldStore';
import { MenuFit, RideStrikes } from '../view/logic/worldFit';
import { stepDown, stepUp } from '../view/logic/compose';
import { compositionFor } from '../view/logic/deviceClass';
import type { QualitySetting } from '../ui/logic/settings';
import { REGISTRY, type Composition, type ModuleId } from '../view/logic/registry';
import { LoadTracker } from './loadTracker';
import { DemoPlayer } from './howto';
import { PristineCache } from './pristineCache';
import { FLEET, bikeForLevel, bikeSpec } from '../sim/bikes';
import { raiseTextureLimits } from '../view/gpuLimits';
import { canFilm, cineActive, cineTick, recordCinematic } from './cinematic';
import { airs } from '../game/cinematic';
import { longestAir } from '../game/highlights';
import { resultTime } from '../ui/video';
import { formatTime } from '../ui/logic/format';
import { HintCoach, hintGlyphs, type InputDevice } from '../ui/logic/hints';

const params = new URLSearchParams(location.search);
const DEV_PARAMS = ['view', 'cam', 'demo', 'warp', 'frames', 'gputime', 'prefetch', 'shotcam'];
if (DEV_PARAMS.some((k) => params.has(k))) {
  location.replace(`${import.meta.env.BASE_URL}dev.html${location.search}`);
  await new Promise(() => {}); // stop here while the browser navigates
}

const PENDING_KEY = 'megamx.pendingRide';
interface Pending { ride: Ride; autostart: boolean }
function takePending(): Pending | null {
  try {
    const raw = sessionStorage.getItem(PENDING_KEY);
    sessionStorage.removeItem(PENDING_KEY);
    return raw ? (JSON.parse(raw) as Pending) : null;
  } catch { return null; }
}

const touchMode = params.get('touch') === '1' || matchMedia('(pointer: coarse)').matches;
const ui = new GameUi(touchMode);
// QA / screenshot entry: ?bike=<fleet id> picks that bike for its discipline (track or enduro), with its brand plastics.
const bikeParam = params.get('bike');
if (bikeParam && FLEET.some((b) => b.id === bikeParam)) {
  const b = bikeSpec(bikeParam);
  ui.updateSettings(b.discipline === 'enduro' ? { enduroModel: b.id } : { model: b.id, bike: b.livery });
}
const pending = takePending();
const requested = params.get('level');
const levelId: LevelId = pending?.ride.level ?? (requested && requested in LEVELS ? (requested as LevelId) : 'ring');

// The loading bar: every load step (src/ui/logic/loadPlan.ts) moves it at its start and end, weighted by what it
// costs; in between it creeps on a compositor transition. Each step is timed and has a timeout (src/app/loadTracker.ts).
const tracker = new LoadTracker((m) => ui.setLoading(m));
const step = tracker.step;

await step('fonts', () => Promise.race([document.fonts?.ready, new Promise((r) => setTimeout(r, 2500))]));

// gpums=1: GPU timestamp queries (scripts/smooth.mjs reads them; costs a little, off by default).
// The discrete GPU is asked for on both paths: WebGPU through the adapter request (three passes powerPreference),
// WebGL2 through a context we create ourselves (three's WebGL backend does not pass powerPreference). Chrome and
// Edge on Windows currently ignore the WebGPU hint (measured 2026-09-29: an Iris Xe + RTX A4000 laptop still gets
// the Iris adapter); the Windows per-app GPU setting decides there.
const makeRenderer = (webgl: boolean) => {
  let canvas: HTMLCanvasElement | undefined, context: WebGL2RenderingContext | undefined;
  if (webgl) {
    canvas = document.createElement('canvas');
    context = canvas.getContext('webgl2', { powerPreference: 'high-performance', antialias: false, alpha: true, depth: true, stencil: false }) ?? undefined;
  }
  const r = new THREE.WebGPURenderer({ antialias: false, forceWebGL: webgl, powerPreference: 'high-performance', trackTimestamp: params.get('gpums') === '1', ...(context ? { canvas, context } : {}) });
  if (!webgl) raiseTextureLimits(); // before init: wraps the adapter request three.js makes
  r.setSize(innerWidth, innerHeight);
  r.domElement.classList.add('mx-canvas');
  // iOS and Android drop the WebGL context under memory pressure: a retry card beats a frozen canvas.
  r.domElement.addEventListener('webglcontextlost', (e) => { e.preventDefault(); ui.loadFailed('network', 'WebGL context lost'); }, { once: true });
  return r;
};
/** One line for the error screen: what failed and where (browser, backend), so a screenshot is a bug report. */
const errText = (err: unknown) => {
  const done = Object.keys(tracker.times);
  // Where it threw: the first two stack frames (minified asset:line:col still pins the module) and the last finished step.
  const at = err instanceof Error && err.stack ? err.stack.split('\n').filter((l) => /\.js|:\d+:\d+/.test(l)).slice(0, 2).map((l) => l.trim().replace(/^at\s+/, '').replace(/https?:\/\/[^/]+\//, '')).join(' < ') : '';
  return `${err instanceof Error ? `${err.name}: ${err.message}` : String(err)} · after "${done[done.length - 1] ?? '-'}" · ${at} · ${navigator.userAgent.match(/(iPhone|iPad|Android|Windows|Mac OS X)[^;)]*/)?.[0] ?? ''} · ${(renderer as unknown as { backend?: { isWebGPUBackend?: boolean } }).backend?.isWebGPUBackend ? 'WebGPU' : 'WebGL2'}`.slice(0, 500);
};
// iPhone / iPad (every iOS browser is WebKit): WebGPU there runs out of buffer memory while the forest loads (createBuffer
// returns an empty mapping: RangeError in three's createAttribute, seen on a real iPhone 2026-10-03). WebGL2 is solid there.
// ?backend=webgpu forces WebGPU for tests.
const IOS = /iPhone|iPad|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
const START_WEBGL = params.get('backend') === 'webgl' || (IOS && params.get('backend') !== 'webgpu');
let renderer = makeRenderer(START_WEBGL);
document.body.prepend(renderer.domElement);
let session: GameSession;
// Two ways to fail, told apart for the player: no WebGPU / WebGL at all (another browser helps), or a code / asset
// download that failed (a network hiccup or a stale deploy: a reload helps).
// Init retries once on WebGPU (a rare headless start failed the adapter/device request for no lasting reason), then
// falls back to WebGL2, before telling the player to try another browser.
await step('renderer', async () => {
  const initAttempts = START_WEBGL ? [true] : [false, false, true];
  for (let i = 0; ; i++) {
    try {
      await Promise.race([renderer.init(), new Promise((_, no) => setTimeout(() => no(new Error('renderer init timed out')), 15000))]);
      break;
    } catch (err) {
      console.warn(`renderer init failed (attempt ${i + 1}, ${initAttempts[i] ? 'WebGL2' : 'WebGPU'})`, err);
      if (i + 1 >= initAttempts.length) {
        ui.loadFailed('gpu', errText(err));
        throw err;
      }
      renderer.domElement.remove();
      renderer = makeRenderer(initAttempts[i + 1]!);
      document.body.prepend(renderer.domElement);
    }
  }
});
const isWebGpu = () => !!(renderer.backend as { isWebGPUBackend?: boolean }).isWebGPUBackend;
// A load that fails on WebGPU (a GPU allocation the device refuses, a pipeline it rejects: iPhones threw a RangeError
// here) is retried once from scratch on WebGL2 before the error screen. A download error is not: WebGL2 would fail
// the same way. QA: ?gpufail=1 makes the WebGPU attempt fail after loading.
for (let attempt = 0; ; attempt++) {
  try {
    session = await GameSession.create(renderer, levelId, ui.settings.quality, bikeForLevel(levelId, ui.settings), step);
    if (params.get('gpufail') === '1' && isWebGpu()) throw new RangeError('simulated WebGPU failure (?gpufail=1)');
    // Every fleet model loads and compiles now, behind the loading screen: done in the background after the menu
    // was up, it cost 100-200 ms hitches (glTF parse, pipeline compiles, a 30 MB GC) in the menu and the first ride.
    await step('fleet', () => session.buildSessionModules());
    break;
  } catch (err) {
    if (attempt === 0 && isWebGpu() && !isDownloadError(err)) {
      console.warn('[load] failed on WebGPU, retrying on WebGL2', err);
      const failed = renderer;
      renderer = makeRenderer(true);
      failed.setAnimationLoop(null);
      failed.domElement.replaceWith(renderer.domElement);
      try { failed.dispose(); } catch { /* a lost device: nothing left to free */ }
      try {
        await step('renderer', () => renderer.init());
        continue;
      } catch (err2) { console.error(err2); }
    }
    console.error(err);
    ui.loadFailed(isDownloadError(err) ? 'network' : 'gpu', errText(err));
    throw err;
  }
}
console.info(`[load] total ${Math.round(tracker.elapsed)} ms`, tracker.times);
ui.setLoading({ from: 1, to: 1, ms: 0, stage: 'ui.loading.ready' });
/** A failed fetch / dynamic import / asset load, as opposed to the GPU refusing to work. */
function isDownloadError(err: unknown): boolean {
  const text = err instanceof Error ? `${err.name} ${err.message}` : String(err);
  return /fetch|dynamically imported module|import|network|load|404|Failed to|ProgressEvent/i.test(text) || (err instanceof Event);
}
(window as unknown as { __mx: unknown }).__mx = { renderer, session, ui };

const keyboard = new KeyboardInput(window);
const touch = new TouchInput(document, params.get('touch') === '1');
const relabelTouch = () => {
  const brake = ui.settings.beginner ? 'touch.brake' : 'touch.front';
  for (const [cls, key] of [['.mx-gas', 'touch.gas'], ['.mx-brake', brake], ['.mx-rear', 'touch.rear'], ['.mx-foot', 'touch.foot'],
    ['.mx-stand', 'touch.stand'], ['.mx-reset', 'touch.reset']] as const) {
    const el = document.querySelector<HTMLElement>(cls);
    if (el) el.textContent = ui.tr(key);
  }
};
/** The control mode reaches every input source: Simplified = one brake and the assists (src/sim/assist.ts). */
// A touch screen can show up after load (a 2-in-1 laptop, DevTools device mode): switch the touch layout and controls
// on at the first touch, or when the primary pointer turns coarse.
const enableTouch = () => { if (touch.active && ui.touch) return; touch.enable(); ui.setTouch(true); applyBeginner(ui.settings.beginner); };
addEventListener('pointerdown', (e) => { if (e.pointerType === 'touch') enableTouch(); }, { capture: true, passive: true });
// A system back gesture (an edge swipe on a phone, where the left thumb steers) must not throw the player out of the game
// and reload it. The page holds one extra history entry, and Back becomes the game's own back (pause in a ride, one
// screen up in the menus). On the start screen it passes through so the player can still leave.
// Arm immediately on a touch device. Waiting for the first pointerdown is too late: the browser's edge swipe navigates
// away without delivering that event (measured on the live site, iPhone UA, ride up, no touch yet: history.back()
// left the page for about:blank). Coming back reloads, which is the "the page restarted" report.
let backGuard = false;
const armBackGuard = () => {
  if (backGuard) return;
  backGuard = true;
  history.pushState({ mx: 1 }, '');
};
if (IOS || /Android/i.test(navigator.userAgent) || matchMedia('(pointer: coarse)').matches) armBackGuard();
addEventListener('pointerdown', (e) => {
  if (e.pointerType === 'touch') armBackGuard();
}, { capture: true, passive: true });
addEventListener('popstate', () => {
  if (!backGuard) return;
  if (ui.state.screen === 'start') { backGuard = false; return; }
  history.pushState({ mx: 1 }, '');
  ui.back();
});
matchMedia('(pointer: coarse)').addEventListener('change', (e) => { if (e.matches) enableTouch(); });
const applyBeginner = (on: boolean) => { keyboard.beginner = on; touch.setBeginner(on); ui.nav.setBeginner(on); relabelTouch(); };
relabelTouch();
applyBeginner(ui.settings.beginner);

let appliedQuality = ui.settings.quality;
const host: UiHost = {
  loadedLevel: levelId,
  progress: () => session.progress(),
  resetProgress: () => session.progressStore.save(emptyProgress()),
  startRide(r) {
    // Another level: the world is built for one, so reload. The bike swaps in place (its discipline's pick).
    if (r.level !== session.levelId) {
      try { sessionStorage.setItem(PENDING_KEY, JSON.stringify({ ride: r, autostart: true } satisfies Pending)); } catch { /* falls back to the menu */ }
      const q = new URLSearchParams(location.search);
      q.set('level', r.level);
      location.search = q.toString();
      return;
    }
    void session.setBike(bikeForLevel(r.level, ui.settings)).then(() => {
      keyboard.reset();
      keyboard.takeReset();
      touch.takeReset();
      session.start(r);
      hints.restart();
      session.prefetchDemos(ui.settings.beginner ? 'simplified' : 'real');
    });
  },
  pause: (on) => session.setPaused(on),
  quitRide: () => session.quit(),
  replay: () => session.startReplay(),
  stopReplay: () => { session.stopReplay(); ui.replayState(false); },
  setCamera: (m) => {
    session.setCamera(m);
    // Out of the garage the shown bike goes back to this level's pick (the garage may preview another discipline).
    if (m !== 'garage') void session.setBike(bikeForLevel(session.levelId, ui.settings));
  },
  previewBike: (id) => session.setBike(id),
  applySettings: (s) => {
    // Another graphics choice applies live, the way the fit does: compose for it and rebuild the changed modules.
    // Auto forgets this device's fitted world and fits again from the class's start cap.
    if (s.quality !== appliedQuality) { appliedQuality = s.quality; void applyQuality(s.quality); }
    session.applySettings(s); applyBeginner(s.beginner);
  },
  howtoClip: (s, mode) => session.demoClip(s, mode),
  playDemo: (clip, slow) => session.playDemo(clip, slow),
  setDemoSlow: (slow) => session.setDemoSlow(slow),
  stopDemo: () => session.stopDemo(),
  practiceFrom: (clip) => session.practiceFrom(clip),
  canMakeVideo: () => canFilm(session),
  rideStats: () => ({
    bestLap: session.rideBestLap,
    air: Math.max(longestAir(session.highlights), ...airs(session.trace).map((a) => a.landing - a.takeoff)),
  }),
  makeVideo: (o, progress, signal, preview) => recordCinematic(session, renderer, {
    ...o, title: ui.tr(`level.${session.levelId}.name` as MessageKey), time: resultTime(ui) ?? (session.rideBestLap !== null ? formatTime(session.rideBestLap) : null), endLine: ui.tr('video.endLine'),
  }, progress, signal, preview),
};
ui.host = host;
ui.hud.setLevel(levelId);
const pristine = new PristineCache(levelId);
DemoPlayer.pristine = pristine.get;
session.events = {
  coach: (key, p) => ui.coach({ key, time: 0, params: p }),
  toast: (key, p, kind) => ui.toast(ui.tr(key, p), kind),
  finish: (r) => ui.finish(r),
  replayEnded: () => ui.replayState(false),
  clipOffer: (h) => ui.clip.offer(h),
  howto: (s) => { const b = session.world.bike.position; pristine.warm(session.world.terrain, b.x, b.z); ui.howto.offer(s); },
  demoEnded: () => ui.howto.demoEnded(),
};

addEventListener('resize', () => {
  renderer.setSize(innerWidth, innerHeight);
  session.view.resize(innerWidth, innerHeight);
});

// ---- contextual one-time hints (src/ui/logic/hints.ts), with the glyphs of the device last used ----
let device: InputDevice = touch.active ? 'touch' : 'keyboard';
addEventListener('keydown', () => { device = 'keyboard'; }, { capture: true, passive: true });
addEventListener('pointerdown', (e) => { if (e.pointerType === 'touch') device = 'touch'; }, { capture: true, passive: true });
const hints = new HintCoach((() => { try { return localStorage; } catch { return null; } })());
let lastRaw: InputFrame | null = null;
function hintFrame(dt: number): void {
  if (!ui.settings.hints || !lastRaw || session.mode !== 'ride' || session.demo || ui.replaying) return;
  const b = session.world.bike, i = lastRaw;
  const id = hints.update({
    assist: ui.settings.beginner, speed: b.telemetry.speed, airborne: b.telemetry.airborne, airTime: b.airTime, pitch: b.telemetry.pitch,
    steer: i.steer, throttle: i.throttle, brake: Math.max(i.brake, i.frontBrake ?? 0, i.rearBrake ?? 0), lean: i.body, limit: session.world.speedLimit, trick: session.world.trick,
  }, dt);
  if (id) ui.hud.coach(ui.tr(`hint.${id}` as MessageKey, hintGlyphs(device, { gas: ui.tr('touch.gas'), brake: ui.tr('touch.brake'), stick: ui.tr('ctrl.touch.stick') })));
}

// ---- input for a physics step ----
function readInput(dt: number): InputFrame {
  const merged = touch.merge(keyboard.read(dt), dt);
  const pad = ui.nav.padInput;
  const i = pad && padRiding(pad) ? { ...pad, standing: pad.standing || merged.standing }
    : pad ? { ...merged, standing: merged.standing || pad.standing } : merged;
  // Simplified mode: the world runs the assists on this intent (recorded in the replay with it).
  if (ui.settings.beginner) { i.assist = true; i.autoFoot = true; }
  if (pad && padRiding(pad)) device = 'gamepad';
  lastRaw = i;
  return i;
}

// ---- boot into a screen ----
const debugScreen = params.get('ui') as ScreenId | null;
if (pending?.autostart) {
  host.startRide(pending.ride);
  ui.loaded('play', pending.ride);
} else if (debugScreen) {
  bootDebug(debugScreen);
} else ui.loaded('start');

/** Screenshot / QA entry: ?ui=<screen>[&ride=throttle|brake|figure8|oval|free|trial|enduro]. */
function bootDebug(to: ScreenId): void {
  const r = params.get('ride');
  const ride: Ride = r === 'free' || r === 'trial' ? { level: 'track', kind: r } : r === 'enduro' ? { level: 'enduro', kind: 'enduro' }
    : r === 'oval' ? { level: 'ring', kind: 'oval' }
    : { level: 'ring', kind: 'drill', drill: r === 'brake' || r === 'figure8' ? r : 'throttle' };
  if (to === 'play' || to === 'pause') {
    host.startRide(ride);
    ui.loaded('play', ride);
    if (to === 'pause') setTimeout(() => ui.dispatch({ type: 'pause' }), Number(params.get('pauseAfter') ?? 4000));
    return;
  }
  if (to === 'results') {
    ui.loaded('start');
    ui.go('levels');
    ui.ride = ride;
    const fake: RideResult = ride.kind === 'drill'
      ? { ride, stars: 2, failed: false, newBest: true, unlocked: ['track'], replayable: false,
        drill: { stars: 2, metrics: { speed: 41.3, distance: 0.84 }, reasons: ['result.reason.distance'] } }
      : { ride, stars: 2, failed: false, laps: [88.41, 84.02, 85.77], total: 258.2, newBest: true, unlocked: ['enduro'], replayable: false };
    ui.dispatch({ type: 'go', to: 'intro' });
    ui.dispatch({ type: 'go', to: 'play' });
    ui.finish(fake);
    return;
  }
  ui.loaded(to === 'intro' ? 'intro' : 'start', ride);
  if (to === 'levels' || to === 'garage' || to === 'settings' || to === 'credits') ui.go(to);
  if (to === 'drills') { ui.go('levels'); ui.go('drills'); }
  if (params.get('help') === '1') ui.dispatch({ type: 'help', open: true });
  // QA: ?ui=garage&click=<fleet id> clicks that bike's card after the garage is up (the live model swap path).
  const click = params.get('click');
  if (click) setTimeout(() => document.querySelector<HTMLElement>(`[data-bike="${click}"]`)?.click(), 800);
}

// ---- world fit (src/view/logic/worldFit.ts): once per device, in the menu; ride strikes step down between rides ----
let menuFit: MenuFit | null = session.fit.final ? null : new MenuFit(session.fit.dc.frameTarget);
const strikes = new RideStrikes(session.fit.dc.frameTarget);
let fitting = false, pendingStepDown = false;
let lastRide: object | null = null;
const finishFit = (c: Composition): void => { saveWorld(storage(), session.fit.key, { composition: c, final: true }); session.fit.final = true; menuFit = null; };
async function applyComposition(next: Composition, final: boolean): Promise<void> {
  fitting = true;
  try {
    const cur = session.view.world.composition;
    // (farShadows only records its level: the sky's cascades follow on the next load, farShadows.ts.)
    for (const id of Object.keys(next) as ModuleId[]) if (next[id] !== cur[id]) await session.view.setModuleLevel(id, next[id]);
    saveWorld(storage(), session.fit.key, { composition: next, final });
  } finally { fitting = false; }
}
async function applyQuality(q: QualitySetting): Promise<void> {
  while (fitting) await new Promise((r) => setTimeout(r, 50));
  const auto = q === 'auto';
  clearWorld(storage(), session.fit.key);
  session.fit.auto = auto; session.fit.final = !auto; pendingStepDown = false;
  await applyComposition(compositionFor(q, session.fit.dc, session.fit.coreMb), !auto);
  menuFit = auto ? new MenuFit(session.fit.dc.frameTarget) : null;
}
async function fitFrame(dt: number, inMenu: boolean, riding: boolean): Promise<void> {
  const ride = session.mode === 'menu' ? null : session.ride;
  if (ride !== lastRide) { if (lastRide && strikes.end() && session.fit.auto) pendingStepDown = true; if (ride) strikes.start(); lastRide = ride; }
  if (riding) { strikes.sample(dt); return; }
  if (!inMenu || fitting) return;
  if (pendingStepDown) {
    pendingStepDown = false;
    // Two strikes: one step down, and the fit runs once more from there (spec: "clears final").
    const c = stepDown(REGISTRY, session.view.world.composition);
    if (c) { await applyComposition(c, false); menuFit = new MenuFit(session.fit.dc.frameTarget); }
    return;
  }
  const v = menuFit?.sample(dt);
  if (!v) return;
  console.info(`[fit] p90 ${((menuFit?.lastP ?? 0) * 1000).toFixed(1)} ms, target ${session.fit.dc.frameTarget} fps -> ${v}`);
  const cur = session.view.world.composition;
  const next = v === 'final' ? null : v === 'down' ? stepDown(REGISTRY, cur) : stepUp(REGISTRY, cur, session.fit.dc.memBudgetMb, session.fit.coreMb);
  if (!next) { finishFit(cur); return; }
  await applyComposition(next, false);
  menuFit?.applied();
}

// ---- loop ----
let last = performance.now();
let warmFor = 0;
renderer.setAnimationLoop(() => {
  const now = performance.now();
  const dt = (now - last) / 1000;
  last = now;
  ui.nav.poll(now);
  const playing = ui.state.screen === 'play';
  const reset = playing && (keyboard.takeReset() || touch.takeReset() || ui.nav.takePadReset());
  if (!playing) { keyboard.takeReset(); touch.takeReset(); }
  if (keyboard.takeCamera() && playing && !ui.state.help) ui.toggleCamera();
  const f = session.frame(dt, readInput, reset);
  if (cineActive()) { cineTick(dt); return; }
  void fitFrame(dt, ui.state.screen === 'start' && !session.demo, playing && session.mode === 'ride' && !session.demo && !ui.replaying);
  document.body.classList.toggle('cam-fpv', session.view.fpvActive && playing);
  ui.clip.frame(playing && session.mode === 'ride' && canFilm(session));
  ui.howto.frame(dt, session.demo?.overlay() ?? null);
  // Ridden chunks' untouched ground is prepared in the background, so a how-to demo starts without a stall.
  if (playing && (warmFor -= dt) <= 0) { warmFor = 2; const b = session.world.bike.position; pristine.warm(session.world.terrain, b.x, b.z); }
  if (f && (playing || ui.replaying || ui.state.screen === 'pause')) {
    const ride = session.ride;
    if (ride) {
      f.title = ride.kind === 'drill' ? ui.tr(`drill.${ride.drill}.name` as MessageKey)
        : ride.kind === 'oval' ? `${ui.tr('ui.oval.name')} · ${ui.tr('ui.mode.free')}`
        : ride.kind === 'enduro' ? ui.tr('level.enduro.name')
          : `${ui.tr('level.track.name')} · ${ride.kind === 'free' ? ui.tr('ui.mode.free') : ui.tr('results.trial')}`;
      const c = session.hudCounters();
      const parts: string[] = [];
      if (c.loops) parts.push(ui.tr('hud.loops', { n: c.loops[0], total: c.loops[1] }));
      if (c.lap) parts.push(ui.tr('hud.lap', { n: c.lap[0], total: c.lap[1] || '∞' }));
      if (c.cp) parts.push(ui.tr('hud.cp', { n: c.cp[0], total: c.cp[1] }));
      f.progress = parts.join('  ·  ') || undefined;
    }
    if (ui.replaying) f.title = ui.tr('ui.replaying');
    f.rider = { x: session.world.bike.position.x, z: session.world.bike.position.z };
    ui.hud.update(f, dt);
    if (playing && !ui.state.help) hintFrame(dt);
  }
});
